import React, { useState, useEffect, useCallback } from 'react';
import { KeyRound, Plus, Trash2, Copy, Check, Loader2, Terminal, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ApiAccessTab = ({ authToken }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null); // full key shown once
  const [copied, setCopied] = useState(false);

  const headers = { 'Authorization': `Bearer ${authToken}` };

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/api-keys`, { headers });
      if (res.ok) setKeys(await res.json());
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, [authToken]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const createKey = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/api-keys`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() || 'Hermes Agent' })
      });
      const data = await res.json();
      if (res.ok) {
        setNewKey(data.api_key); setShowForm(false); setName('');
        fetchKeys();
      } else toast.error(data.detail || 'Failed to create key');
    } catch (e) { toast.error('Failed to create key'); } finally { setCreating(false); }
  };

  const revokeKey = async (id) => {
    if (!window.confirm('Revoke this API key? The connected agent will lose access immediately.')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/api-keys/${id}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Key revoked'); fetchKeys(); }
      else toast.error('Failed to revoke');
    } catch (e) { toast.error('Failed to revoke'); }
  };

  const copyKey = () => { navigator.clipboard.writeText(newKey); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const fmt = (s) => s ? new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
  const curlExample = `curl -H "X-API-Key: YOUR_KEY" \\\n  ${API_URL}/api/stories`;

  return (
    <div data-testid="api-access-tab">
      {/* Connection guide */}
      <div className="border border-white/10 p-6 bg-white/[0.02] mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Terminal className="w-5 h-5 text-white/60" />
          <h3 className="text-white font-medium">Connect your Hermes / OpenClaw agent</h3>
        </div>
        <p className="text-white/50 text-sm mb-4">
          Generate a key below and add it to your agent. Send it on every request as an <code className="text-white/80 font-mono">X-API-Key</code> header
          (or as a <code className="text-white/80 font-mono">Bearer</code> token). The key grants full content management — stories, videos,
          wedding films, podcast series, tags, service images and inquiries.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 text-sm mb-4">
          <div>
            <p className="text-white/40 text-xs uppercase mb-1">Base URL</p>
            <code className="block text-white/80 bg-black/40 border border-white/10 p-2 font-mono break-all text-xs">{API_URL}</code>
          </div>
          <div>
            <p className="text-white/40 text-xs uppercase mb-1">Auth header</p>
            <code className="block text-white/80 bg-black/40 border border-white/10 p-2 font-mono text-xs">X-API-Key: khan_…</code>
          </div>
        </div>
        <p className="text-white/40 text-xs uppercase mb-1">Example request</p>
        <pre className="text-white/70 bg-black/40 border border-white/10 p-3 font-mono text-xs overflow-x-auto whitespace-pre-wrap">{curlExample}</pre>
        <p className="text-white/40 text-xs mt-3">
          Common endpoints: <span className="text-white/60 font-mono">POST /api/admin/stories</span>, <span className="text-white/60 font-mono">POST /api/admin/videos</span>,
          <span className="text-white/60 font-mono"> POST /api/admin/wedding-films</span>, <span className="text-white/60 font-mono">GET /api/stories</span>.
        </p>
      </div>

      {/* Header + create */}
      <div className="flex justify-between items-center mb-6">
        <p className="text-white/50 text-sm">{keys.length} key{keys.length !== 1 ? 's' : ''}</p>
        <button data-testid="create-key-btn" onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-medium">
          <Plus className="w-4 h-4" />Generate Key
        </button>
      </div>

      {showForm && (
        <div className="border border-white/10 p-6 mb-6 bg-white/[0.02]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-white font-medium">New API Key</h3>
            <button onClick={() => setShowForm(false)} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <label className="text-white/50 text-xs uppercase mb-2 block">Label (to recognise this key)</label>
          <div className="flex gap-3">
            <input data-testid="key-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Hermes Production" className="flex-1 bg-white/[0.03] border border-white/10 px-4 py-2.5 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-white/30" />
            <button data-testid="key-create-confirm" onClick={createKey} disabled={creating} className="px-6 py-2.5 bg-white text-black text-sm font-medium disabled:opacity-50">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* Keys list */}
      {loading ? (
        <div className="flex items-center gap-2 text-white/50 py-10"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
      ) : keys.length === 0 ? (
        <div className="text-center py-16 border border-white/10">
          <KeyRound className="w-10 h-10 text-white/15 mx-auto mb-3" />
          <p className="text-white/40">No API keys yet</p>
          <p className="text-white/30 text-sm mt-1">Generate one to connect your agent</p>
        </div>
      ) : (
        <div className="space-y-3">
          {keys.map(k => (
            <div key={k.id} data-testid={`api-key-${k.id}`} className={`border p-4 flex items-center justify-between gap-4 ${k.is_active ? 'border-white/10 bg-white/[0.02]' : 'border-white/5 bg-transparent opacity-60'}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white font-medium text-sm">{k.name}</span>
                  {!k.is_active && <span className="px-2 py-0.5 bg-red-500/15 text-red-300 text-xs">Revoked</span>}
                </div>
                <code className="text-white/40 text-xs font-mono">{k.key_preview}</code>
                <p className="text-white/30 text-xs mt-1">Created {fmt(k.created_at)} · Last used {fmt(k.last_used_at)}</p>
              </div>
              {k.is_active && (
                <button data-testid={`revoke-key-${k.id}`} onClick={() => revokeKey(k.id)} className="p-2 text-white/30 hover:text-red-400 flex-shrink-0" title="Revoke key">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* New key reveal modal */}
      {newKey && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6" data-testid="new-key-modal">
          <div className="bg-[#0f0f0f] border border-white/10 max-w-lg w-full p-6">
            <div className="flex items-center gap-2 text-amber-400 mb-3">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="text-white font-medium">Copy your API key now</h3>
            </div>
            <p className="text-white/50 text-sm mb-4">This is the only time the full key will be shown. Store it in your agent's secret config.</p>
            <code className="block text-emerald-300 text-sm break-all bg-black/40 border border-white/10 p-3 font-mono mb-4" data-testid="new-key-value">{newKey}</code>
            <div className="flex gap-3">
              <button data-testid="copy-key-btn" onClick={copyKey} className="flex items-center gap-2 px-4 py-2.5 border border-white/20 text-white/70 hover:text-white text-sm">
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}{copied ? 'Copied' : 'Copy key'}
              </button>
              <button data-testid="new-key-done" onClick={() => setNewKey(null)} className="px-5 py-2.5 bg-white text-black text-sm font-medium">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiAccessTab;
